import { validateAddress } from "@zebraex/asset-utils/validation";
import { type AssetTypes, type TFullMarketInfo } from "@zebraex/server/export";
import { z } from "zod";
import { immer } from "zustand/middleware/immer";
import { createStore } from "zustand/vanilla";

type TAssetSelectionData = {
    data?: AssetTypes.TAssetSelect;
    amount: number;
};

type TLastEdited = "assetFrom" | "assetTo";

type TOrderFormState = {
    marketsList: TFullMarketInfo[];

    selection: {
        lastEdited?: TLastEdited;
        assetFrom: TAssetSelectionData;
        assetTo: TAssetSelectionData;
        market?: TFullMarketInfo;
    };

    customerInfo: {
        checkConsent: boolean;
        checkPrivacy: boolean;
        email: string;
        lineOne: string;
        lineTwo: string;
        lineThree: string;
    };
};

type TOrderFormActions = {
    setMarketList: (data: TFullMarketInfo[]) => void;
    selection: {
        swapAssets: () => void;
        setLastEdited: (v: TLastEdited) => void;
        assetFrom: {
            setData: (v: AssetTypes.TAssetSelect) => void;
            setAmount: (v: number) => void;
        };
        assetTo: {
            setData: (v: AssetTypes.TAssetSelect) => void;
            setAmount: (v: number) => void;
        };
        setMarket: (v: TFullMarketInfo) => void;
    };
    customerInfo: {
        setCheckConsent: (v: boolean) => void;
        setCheckPrivacy: (v: boolean) => void;
        setEmail: (v: string) => void;
        setLineOne: (v: string) => void;
        setLineTwo: (v: string) => void;
        setLineThree: (v: string) => void;
    };
};

type TOrderForm = TOrderFormState & TOrderFormActions;

function isValidSelection(state: TOrderForm) {
    const market = state.selection.market;
    const amountFrom = state.selection.assetFrom.amount;
    const amountTo = state.selection.assetTo.amount;
    if (!market) {
        return false;
    }
    if (!amountFrom) {
        return false;
    }
    if (!amountTo) {
        return false;
    }
    if (amountFrom > Number(market.max_from)) {
        return false;
    }
    if (amountFrom < Number(market.min_from)) {
        return false;
    }
    // if (amountTo > Number(market.max_to)) {
    //   return false;
    // }
    // if (amountTo < Number(market.min_to)) {
    //   return false;
    // }
    return true;
}

function validateAddress_(a: string, b: any) {
    return validateAddress(a, b);
}

function isValidForm(state: TOrderForm) {
    const selectionValid = isValidSelection(state);
    const addressValid = selectionValid ? validateAddress_(state.customerInfo.lineOne, state.selection.assetTo.data) : false;
    const emailValid = z.string().email().safeParse(state.customerInfo.email).success;
    const consentValid = state.customerInfo.checkConsent;
    const customFieldValid = !(
        state.selection.assetTo.data?.kind !== "crypto" &&
        (state.customerInfo.lineTwo === "" || state.customerInfo.lineTwo === undefined)
    );

    const validations = {
        selectionValid,
        addressValid,
        emailValid,
        consentValid,
        customFieldValid,
    };

    return {
        result: !Object.values(validations).some((v) => v === false),
        validations,
    };
}

function findInverseMarket(params: {
    marketsList?: TFullMarketInfo[];
    assetTo?: AssetTypes.TAssetSelect;
    assetFrom?: AssetTypes.TAssetSelect;
}): TFullMarketInfo | undefined {
    const { marketsList, assetTo, assetFrom } = params;
    if (!marketsList) {
        return undefined;
    } else {
        if (assetTo === undefined && assetFrom === undefined) {
            return undefined;
        }

        if (assetTo && assetFrom === undefined) {
            return marketsList.find((m) => m.asset_from === assetTo.id);
        }

        if (assetTo === undefined && assetFrom) {
            return marketsList.find((m) => m.asset_to === assetFrom.id);
        }

        return marketsList.find((m) => m.asset_to === assetFrom!.id && m.asset_from === assetTo!.id);
    }
}

function calculateMarket(params: { market: TFullMarketInfo; amountFrom?: number; amountTo?: number }) {
    const { market, amountFrom, amountTo } = params;
    if (!amountFrom && !amountTo) {
        return { amountFrom: 0, amountTo: 0 };
    } else if (amountFrom) {
        return {
            amountFrom,
            amountTo: parseFloat((amountFrom * market.price.market_price).toFixed(Math.min(market.asset_to_data.precision, 8))),
        };
    } else {
        return {
            amountFrom: parseFloat((amountTo! / market.price.market_price).toFixed(Math.min(market.asset_from_data.precision, 8))),
            amountTo: amountTo!,
        };
    }
}

const create = () =>
    createStore<TOrderForm>()(
        immer((set) => ({
            marketsList: [],
            setMarketList: (v) => set({ marketsList: v }),
            selection: {
                swapAssets: () =>
                    set((state) => {
                        const assetFrom = state.selection.assetFrom.data;
                        const assetTo = state.selection.assetTo.data;
                        const inverse = findInverseMarket({ marketsList: state.marketsList, assetFrom, assetTo });
                        if (inverse) {
                            if (state.selection.lastEdited === "assetFrom") {
                                // keep the amount of assetFrom and assign it to assetTo, assign asset_to to assetFrom asset, recalculate new assetFrom
                                const { amountFrom } = calculateMarket({ market: inverse, amountTo: state.selection.assetFrom.amount });
                                state.selection.assetTo.amount = state.selection.assetFrom.amount;
                                state.selection.assetFrom.amount = amountFrom;
                                state.selection.lastEdited = "assetTo";
                            } else if (state.selection.lastEdited === "assetTo") {
                                // keep the amount of assetTo and assign it to assetFrom, assign asset_from to assetTo asset, recalculate new assetTo
                                const { amountTo } = calculateMarket({ market: inverse, amountFrom: state.selection.assetTo.amount });
                                state.selection.assetFrom.amount = state.selection.assetTo.amount;
                                state.selection.assetTo.amount = amountTo;
                                state.selection.lastEdited = "assetFrom";
                            }
                            state.selection.market = inverse;
                            state.selection.assetFrom.data = inverse.asset_from_data;
                            state.selection.assetTo.data = inverse.asset_to_data;
                        }
                    }),
                setLastEdited: (v) =>
                    set((state) => {
                        state.selection.lastEdited = v;
                    }),
                assetFrom: {
                    data: undefined,
                    amount: 0,
                    setData: (v) =>
                        set((state) => {
                            state.selection.assetFrom.data = v;
                            // check if new selection leads to an valid market
                            // if not -> unset market and assetTo selection
                            if (state.selection.assetTo.data) {
                                const validMarket = state.marketsList.find((m) => m.asset_to === state.selection.assetTo.data!.id && m.asset_from === v.id);

                                if (state.selection.lastEdited !== "assetTo" || !validMarket) {
                                    state.selection.assetTo.amount = 0;
                                } else {
                                    const { amountFrom } = calculateMarket({ market: validMarket, amountTo: state.selection.assetTo.amount });
                                    state.selection.assetFrom.amount = amountFrom;
                                }

                                if (!validMarket) {
                                    state.selection.market = undefined;
                                    state.selection.assetTo.data = undefined;
                                } else {
                                    state.selection.market = validMarket;
                                    state.selection.assetTo.data = validMarket.asset_to_data;
                                }

                                if (state.selection.lastEdited !== "assetFrom" && !validMarket) {
                                    state.selection.assetFrom.amount = 0;
                                }
                            }
                        }),
                    setAmount: (v) =>
                        set((state) => {
                            if (state.selection.assetFrom.data) {
                                state.selection.lastEdited = "assetFrom";
                                state.selection.assetFrom.amount = v;
                                if (state.selection.market !== undefined) {
                                    const { amountTo } = calculateMarket({ market: state.selection.market, amountFrom: v });
                                    state.selection.assetTo.amount = amountTo;
                                }
                            }
                        }),
                },
                assetTo: {
                    data: undefined,
                    amount: 0,
                    setData: (v) =>
                        set((state) => {
                            state.selection.assetTo.data = v;

                            if (state.selection.assetFrom.data) {
                                const validMarket = state.marketsList.find((m) => m.asset_from === state.selection.assetFrom.data!.id && m.asset_to === v.id);

                                if (state.selection.lastEdited !== "assetFrom" || !validMarket) {
                                    state.selection.assetFrom.amount = 0;
                                } else {
                                    const { amountTo } = calculateMarket({ market: validMarket, amountFrom: state.selection.assetFrom.amount });
                                    state.selection.assetTo.amount = amountTo;
                                }

                                if (!validMarket) {
                                    state.selection.market = undefined;
                                    state.selection.assetFrom.data = undefined;
                                } else {
                                    state.selection.market = validMarket;
                                    state.selection.assetFrom.data = validMarket.asset_from_data;
                                }

                                if (state.selection.lastEdited !== "assetTo" && !validMarket) {
                                    state.selection.assetTo.amount = 0;
                                }
                            }
                        }),
                    setAmount: (v) =>
                        set((state) => {
                            if (state.selection.assetTo.data) {
                                state.selection.lastEdited = "assetTo";
                                state.selection.assetTo.amount = v;
                                if (state.selection.market !== undefined) {
                                    const { amountFrom } = calculateMarket({ market: state.selection.market, amountTo: v });
                                    state.selection.assetFrom.amount = amountFrom;
                                }
                            }
                        }),
                },
                market: undefined,
                setMarket: (v) =>
                    set((state) => {
                        state.selection.market = v;
                    }),
            },
            customerInfo: {
                checkConsent: false,
                checkPrivacy: false,
                email: "",
                lineOne: "",
                lineTwo: "",
                lineThree: "",
                setCheckConsent: (v) =>
                    set((state) => {
                        state.customerInfo.checkConsent = v;
                    }),
                setCheckPrivacy: (v) =>
                    set((state) => {
                        state.customerInfo.checkPrivacy = v;
                    }),
                setEmail: (v) =>
                    set((state) => {
                        state.customerInfo.email = v;
                    }),
                setLineOne: (v) =>
                    set((state) => {
                        state.customerInfo.lineOne = v;
                    }),
                setLineTwo: (v) =>
                    set((state) => {
                        state.customerInfo.lineTwo = v;
                    }),
                setLineThree: (v) =>
                    set((state) => {
                        state.customerInfo.lineThree = v;
                    }),
            },
        })),
    );

export { create, findInverseMarket, isValidForm, isValidSelection, validateAddress_ as validateAddress };
export type { TAssetSelectionData, TLastEdited, TOrderForm };
